import React, { useState } from 'react';
import {
  TrendingUp,
  PieChart,
  Activity,
  Calendar,
  ArrowUpRight,
  Sparkles,
  ShieldCheck,
} from 'lucide-react';

export const FinancialTerminal: React.FC = () => {
  const [activeTab, setActiveTab] = useState<'revenue' | 'cashflow' | 'profit' | 'kpis'>('cashflow');
  const [runwayDays, setRunwayDays] = useState(84);

  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN'];
  const revenues = [32, 35, 38, 41, 40.4, 46.2]; // in Lakhs

  // Compute slider fill percentage (range 30 to 180)
  const sliderPct = Math.min(100, Math.max(0, ((runwayDays - 30) / (180 - 30)) * 100));

  return (
    <div
      className="glass-panel"
      style={{
        position: 'relative',
        borderRadius: '18px',
        border: '1px solid var(--border-medium)',
        backgroundColor: 'var(--bg-card)',
        boxShadow: 'var(--shadow-elevated)',
        overflow: 'hidden',
      }}
    >
      {/* Top Terminal Bar */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          padding: '12px 18px',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ff5f56' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#ffbd2e' }} />
          <div style={{ width: '10px', height: '10px', borderRadius: '50%', backgroundColor: '#27c93f' }} />
          <span
            className="font-mono"
            style={{
              fontSize: '0.78rem',
              color: 'var(--text-muted)',
              marginLeft: '8px',
              display: 'flex',
              alignItems: 'center',
              gap: '6px',
            }}
          >
            <span
              style={{
                width: '6px',
                height: '6px',
                borderRadius: '50%',
                backgroundColor: 'var(--signal-green)',
                boxShadow: '0 0 6px var(--signal-green)',
              }}
            />
            FORTY2_INTELLIGENCE_ENGINE // FY26
          </span>
        </div>

        {/* Health Score Pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            padding: '4px 10px',
            borderRadius: '9999px',
            backgroundColor: 'rgba(0, 230, 118, 0.1)',
            border: '1px solid rgba(0, 230, 118, 0.25)',
          }}
        >
          <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)' }}>Health:</span>
          <span className="font-mono" style={{ fontSize: '0.82rem', fontWeight: 700, color: 'var(--signal-green)' }}>
            78/100 GOOD
          </span>
        </div>
      </div>

      {/* Navigation Sub-Tabs Bar */}
      <div
        style={{
          display: 'flex',
          borderBottom: '1px solid var(--border-subtle)',
          backgroundColor: 'var(--bg-surface)',
          padding: '6px 10px',
          gap: '6px',
          overflowX: 'auto',
          scrollbarWidth: 'none',
        }}
      >
        {[
          { id: 'revenue', label: 'Revenue Intelligence', icon: TrendingUp },
          { id: 'cashflow', label: 'Cash Flow & Runway', icon: Calendar },
          { id: 'profit', label: 'Profitability & EBITDA', icon: PieChart },
          { id: 'kpis', label: 'Live Scorecard', icon: Activity },
        ].map((tab) => {
          const Icon = tab.icon;
          const isActive = activeTab === tab.id;
          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id as any)}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                padding: '8px 14px',
                borderRadius: '8px',
                fontSize: '0.82rem',
                fontWeight: isActive ? 700 : 500,
                color: isActive ? '#ffffff' : 'var(--text-secondary)',
                backgroundColor: isActive ? 'var(--brand-red)' : 'transparent',
                border: 'none',
                cursor: 'pointer',
                transition: 'all 0.2s ease',
                whiteSpace: 'nowrap',
              }}
            >
              <Icon size={14} color={isActive ? '#ffffff' : 'var(--text-muted)'} />
              <span>{tab.label}</span>
            </button>
          );
        })}
      </div>

      {/* Terminal View Body */}
      <div style={{ padding: '24px' }}>
        {/* =========================================================================
            TAB 1: REVENUE
            ========================================================================= */}
        {activeTab === 'revenue' && (
          <div>
            <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px', marginBottom: '20px' }}>
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  REVENUE YTD
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', marginTop: '2px' }}>
                  <span className="font-mono" style={{ fontSize: '2.1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    ₹2.4Cr
                  </span>
                  <span className="badge-pill badge-green" style={{ fontSize: '0.75rem', padding: '3px 8px' }}>
                    <ArrowUpRight size={13} /> 18.2% vs PY
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', gap: '16px' }}>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>THIS MONTH</span>
                  <span className="font-mono" style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    ₹46.2L
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--signal-green)', display: 'block' }}>+₹5.8L vs prior</span>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)', display: 'block' }}>TARGET HIT</span>
                  <span className="font-mono" style={{ fontSize: '1.15rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    96.4%
                  </span>
                  <span style={{ fontSize: '0.72rem', color: 'var(--signal-blue)', display: 'block' }}>Pacing on track</span>
                </div>
              </div>
            </div>

            {/* Simulated Interactive Bar Chart */}
            <div style={{ background: 'var(--bg-surface)', borderRadius: '12px', padding: '16px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>
                  MONTHLY RUN RATE (LAKHS)
                </span>
                <span className="font-mono" style={{ fontSize: '0.75rem', color: 'var(--brand-red-light)' }}>
                  Budget Var: -2.1%
                </span>
              </div>

              <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between', height: '110px', gap: '8px', paddingBottom: '4px' }}>
                {revenues.map((rev, idx) => {
                  const heightPercent = (rev / 50) * 100;
                  const isLatest = idx === revenues.length - 1;
                  return (
                    <div key={months[idx]} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', flex: 1, gap: '6px' }}>
                      <span className="font-mono" style={{ fontSize: '0.7rem', color: isLatest ? 'var(--signal-green)' : 'var(--text-muted)' }}>
                        ₹{rev}L
                      </span>
                      <div
                        style={{
                          width: '100%',
                          maxWidth: '42px',
                          height: `${heightPercent}%`,
                          borderRadius: '6px 6px 2px 2px',
                          background: isLatest
                            ? 'linear-gradient(180deg, #00E676 0%, #00B0FF 100%)'
                            : 'linear-gradient(180deg, rgba(229, 57, 53, 0.8) 0%, rgba(183, 28, 28, 0.5) 100%)',
                          boxShadow: isLatest ? '0 0 16px rgba(0, 230, 118, 0.3)' : 'none',
                          transition: 'height 0.4s ease',
                        }}
                      />
                      <span className="font-mono" style={{ fontSize: '0.72rem', color: 'var(--text-secondary)' }}>
                        {months[idx]}
                      </span>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 2: CASH FLOW & RUNWAY (FIXED & POLISHED)
            ========================================================================= */}
        {activeTab === 'cashflow' && (
          <div>
            {/* Top KPI Header with clean non-wrapping layout */}
            <div
              style={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'space-between',
                alignItems: 'center',
                gap: '16px',
                marginBottom: '20px',
              }}
            >
              <div>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.06em', fontWeight: 600 }}>
                  CASH POSITION
                </span>
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginTop: '2px' }}>
                  <span className="font-mono" style={{ fontSize: '2.2rem', fontWeight: 800, color: 'var(--text-primary)', lineHeight: 1 }}>
                    ₹84.2L
                  </span>
                  <span
                    className="font-mono"
                    style={{
                      fontSize: '0.76rem',
                      fontWeight: 700,
                      padding: '4px 10px',
                      borderRadius: '6px',
                      backgroundColor: 'rgba(0, 176, 255, 0.12)',
                      border: '1px solid rgba(0, 176, 255, 0.3)',
                      color: 'var(--signal-blue)',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {runwayDays} DAYS RUNWAY
                  </span>
                </div>
              </div>

              {/* 3 Metric Pills */}
              <div style={{ display: 'flex', gap: '10px' }}>
                <div
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'rgba(0, 230, 118, 0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 230, 118, 0.25)',
                    minWidth: '78px',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: '0.68rem', color: 'var(--signal-green)', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    CASH IN
                  </span>
                  <span className="font-mono" style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    ₹46L
                  </span>
                </div>

                <div
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'rgba(229, 57, 53, 0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(229, 57, 53, 0.25)',
                    minWidth: '78px',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: '0.68rem', color: 'var(--brand-red-light)', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    CASH OUT
                  </span>
                  <span className="font-mono" style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--text-primary)' }}>
                    ₹32L
                  </span>
                </div>

                <div
                  style={{
                    padding: '8px 14px',
                    backgroundColor: 'rgba(0, 176, 255, 0.08)',
                    borderRadius: '8px',
                    border: '1px solid rgba(0, 176, 255, 0.25)',
                    minWidth: '78px',
                    textAlign: 'center',
                  }}
                >
                  <span style={{ fontSize: '0.68rem', color: 'var(--signal-blue)', fontWeight: 700, display: 'block', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                    NET SURPLUS
                  </span>
                  <span className="font-mono" style={{ fontSize: '1rem', fontWeight: 800, color: 'var(--signal-green)' }}>
                    +₹14L
                  </span>
                </div>
              </div>
            </div>

            {/* Interactive Predictive Cash Runway Simulator Box */}
            <div
              style={{
                padding: '18px 20px',
                backgroundColor: 'var(--bg-surface)',
                borderRadius: '12px',
                border: '1px solid var(--border-subtle)',
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <span style={{ fontSize: '0.84rem', fontWeight: 600, color: 'var(--text-secondary)' }}>
                  90-Day Predictive Cash Runway Simulator
                </span>
                <span className="font-mono" style={{ fontSize: '0.82rem', color: 'var(--brand-red-light)', fontWeight: 700 }}>
                  Active Horizon: {runwayDays} days
                </span>
              </div>

              {/* Styled Range Slider */}
              <input
                type="range"
                min="30"
                max="180"
                value={runwayDays}
                onChange={(e) => setRunwayDays(Number(e.target.value))}
                className="terminal-slider"
                style={{
                  background: `linear-gradient(to right, var(--brand-red) 0%, var(--brand-red) ${sliderPct}%, var(--border-bright) ${sliderPct}%, var(--border-bright) 100%)`,
                }}
              />

              <div
                style={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  fontSize: '0.72rem',
                  color: 'var(--text-muted)',
                  marginTop: '8px',
                }}
                className="font-mono"
              >
                <span style={{ color: runwayDays < 45 ? 'var(--brand-red-light)' : 'var(--text-muted)' }}>
                  30 Days (Critical)
                </span>
                <span style={{ color: 'var(--text-primary)', fontWeight: 700 }}>
                  {runwayDays} Days (Current)
                </span>
                <span style={{ color: 'var(--signal-green)' }}>
                  180 Days (Target)
                </span>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 3: PROFITABILITY & EBITDA
            ========================================================================= */}
        {activeTab === 'profit' && (
          <div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(130px, 1fr))', gap: '12px', marginBottom: '16px' }}>
              <div style={{ padding: '14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>EBITDA MARGIN</span>
                <div className="font-mono" style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--signal-green)', marginTop: '2px' }}>
                  23.4%
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--text-secondary)' }}>Target: 20-25%</span>
              </div>

              <div style={{ padding: '14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>GROSS MARGIN</span>
                <div className="font-mono" style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '2px' }}>
                  64.8%
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--signal-green)' }}>▲ +2.4% vs Q1</span>
              </div>

              <div style={{ padding: '14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
                <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>NET PROFIT</span>
                <div className="font-mono" style={{ fontSize: '1.5rem', fontWeight: 800, color: 'var(--text-primary)', marginTop: '2px' }}>
                  ₹18.6L
                </div>
                <span style={{ fontSize: '0.7rem', color: 'var(--brand-red-light)' }}>▼ -1.2% vs budget</span>
              </div>
            </div>

            <div
              style={{
                padding: '14px 16px',
                background: 'rgba(229, 57, 53, 0.08)',
                borderRadius: '10px',
                border: '1px solid var(--border-red)',
                display: 'flex',
                alignItems: 'center',
                gap: '12px',
              }}
            >
              <Sparkles size={18} color="var(--brand-red)" style={{ flexShrink: 0 }} />
              <div style={{ fontSize: '0.82rem', color: 'var(--text-primary)', lineHeight: '1.4' }}>
                <strong>Automated Leakage Alert:</strong> Indirect overheads in logistics spiked 4.2% in May. Categorized and highlighted for CA audit review.
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TAB 4: LIVE SCORECARD / KPIS
            ========================================================================= */}
        {activeTab === 'kpis' && (
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: '12px' }}>
            <div style={{ padding: '12px 14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>DSO (Receivables)</span>
                <span className="badge-pill badge-green" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>Stable</span>
              </div>
              <div className="font-mono" style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                38 days <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)', fontWeight: 400 }}>→</span>
              </div>
            </div>

            <div style={{ padding: '12px 14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Inventory Turnover</span>
                <span className="badge-pill badge-green" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>▲ Fast</span>
              </div>
              <div className="font-mono" style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                6.8x <span style={{ fontSize: '0.75rem', color: 'var(--signal-green)' }}>▲</span>
              </div>
            </div>

            <div style={{ padding: '12px 14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Budget Variance</span>
                <span className="badge-pill badge-red" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>▼ -2.1%</span>
              </div>
              <div className="font-mono" style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                -2.1% <span style={{ fontSize: '0.75rem', color: 'var(--brand-red-light)' }}>▼</span>
              </div>
            </div>

            <div style={{ padding: '12px 14px', background: 'var(--bg-surface)', borderRadius: '10px', border: '1px solid var(--border-subtle)' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <span style={{ fontSize: '0.75rem', color: 'var(--text-muted)' }}>Working Capital</span>
                <span className="badge-pill badge-blue" style={{ fontSize: '0.68rem', padding: '2px 6px' }}>▲ Strong</span>
              </div>
              <div className="font-mono" style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginTop: '4px' }}>
                ₹1.2Cr <span style={{ fontSize: '0.75rem', color: 'var(--signal-blue)' }}>▲</span>
              </div>
            </div>
          </div>
        )}

        {/* =========================================================================
            TERMINAL FOOTER
            ========================================================================= */}
        <div
          style={{
            marginTop: '20px',
            paddingTop: '16px',
            borderTop: '1px solid var(--border-subtle)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: '12px',
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                width: '32px',
                height: '32px',
                borderRadius: '8px',
                backgroundColor: 'rgba(229, 57, 53, 0.12)',
                color: 'var(--brand-red)',
                fontWeight: 800,
                fontSize: '1rem',
                border: '1px solid var(--border-red)',
              }}
            >
              ४२
            </span>
            <div>
              <div style={{ fontSize: '0.8rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                Autonomous Sync Protocol
              </div>
              <div style={{ fontSize: '0.72rem', color: 'var(--text-muted)' }}>
                Tally Prime & Zoho live webhook ingestion
              </div>
            </div>
          </div>

          <div style={{ display: 'flex', gap: '6px' }}>
            <span
              className="badge-pill font-mono"
              style={{
                fontSize: '0.7rem',
                padding: '3px 8px',
                backgroundColor: 'rgba(0, 230, 118, 0.1)',
                border: '1px solid rgba(0, 230, 118, 0.25)',
                color: 'var(--signal-green)',
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                gap: '5px',
              }}
            >
              <span style={{ width: '5px', height: '5px', borderRadius: '50%', backgroundColor: 'var(--signal-green)' }} />
              P&L LIVE
            </span>
            <span
              className="badge-pill font-mono"
              style={{
                fontSize: '0.7rem',
                padding: '3px 8px',
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontWeight: 600,
              }}
            >
              RUNWAY LOCKED
            </span>
            <span
              className="badge-pill font-mono"
              style={{
                fontSize: '0.7rem',
                padding: '3px 8px',
                backgroundColor: 'var(--bg-surface)',
                border: '1px solid var(--border-subtle)',
                color: 'var(--text-secondary)',
                fontWeight: 600,
              }}
            >
              CA MONITORED
            </span>
          </div>
        </div>
      </div>
    </div>
  );
};
